<template>
  <v-expansion-panels v-model="expandedPanels" multiple>
    <!-- URL Panel – shows all URLs for categories -->
    <v-expansion-panel>
      <v-expansion-panel-title>
        Category URLs ({{ urlFilters.displayedUrls.value.length }})
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-row>
          <v-col cols="12">
            <!-- Use the common URL filter chips including sitemap filters -->
            <UrlFilterChips
              :fetch-status-counts="urlFilters.fetchStatusCounts.value"
              :active-fetch-status-filters="urlFilters.activeFetchStatusFilters.value"
              :toggle-fetch-status-filter="urlFilters.toggleFetchStatusFilter"
              show-sitemap-filters
              :sitemap-count="urlFilters.sitemapCount.value"
              :non-sitemap-count="urlFilters.nonSitemapCount.value"
              :active-sitemap-filters="urlFilters.activeSitemapFilters.value"
              :toggle-sitemap-filter="urlFilters.toggleSitemapFilter"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="url in urlFilters.displayedUrls.value"
            :key="url.pathname"
            cols="12"
            sm="6"
            md="4"
          >
            <UrlCard :item="url"/>
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>

    <!-- Processed Categories Panel -->
    <v-expansion-panel v-if="filteredCategories.length > 0">
      <v-expansion-panel-title>
        Processed Categories ({{ filteredCategories.length }})
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-row>
          <v-col cols="12">
            <div class="d-flex flex-wrap gap-2 mb-3">
              <v-chip
                v-if="categoriesStore.missingMetaTitleCount > 0"
                :color="activeFilters.includes('metaTitle') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('metaTitle')"
              >
                Missing Meta Title ({{ categoriesStore.missingMetaTitleCount }})
              </v-chip>
              <v-chip
                v-if="categoriesStore.missingMetaDescriptionCount > 0"
                :color="activeFilters.includes('metaDescription') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('metaDescription')"
              >
                Missing Meta Description ({{ categoriesStore.missingMetaDescriptionCount }})
              </v-chip>
              <v-chip
                v-if="categoriesStore.missingDescriptionCount > 0"
                :color="activeFilters.includes('description') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('description')"
              >
                Missing Description ({{ categoriesStore.missingDescriptionCount }})
              </v-chip>
              <v-chip
                v-if="categoriesStore.missingNameCount > 0"
                :color="activeFilters.includes('name') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('name')"
              >
                Missing Name ({{ categoriesStore.missingNameCount }})
              </v-chip>
              <v-chip
                v-if="categoriesStore.missingProductsPathsCount > 0"
                :color="activeFilters.includes('productsPaths') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('productsPaths')"
              >
                Missing Product Paths ({{ categoriesStore.missingProductsPathsCount }})
              </v-chip>
              <v-chip
                v-if="categoriesStore.missingImagesCount > 0"
                :color="activeFilters.includes('image') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('image')"
              >
                Missing Image ({{ categoriesStore.missingImagesCount }})
              </v-chip>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="category in filteredCategories"
            :key="category.pathname"
            cols="12"
            md="4"
          >
            <CategoryItem :category="category" />
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useCategoriesStore } from '@/store/categoriesStore';
import UrlCard from './UrlCard.vue';
import CategoryItem from './CategoryItem.vue';
import UrlTypes from '@/enums/UrlTypes';
import UrlFilterChips from '@/components/UrlFilterChips.vue';
import { useActiveFilters } from '@/composables/useActiveFilters';
import { applyMissingFilters } from '@/helpers/filterMissing';
import { useUrlFilters } from '@/composables/useUrlFilters';

const categoriesStore = useCategoriesStore();
const { activeFilters, toggleFilter } = useActiveFilters();

const filteredCategories = computed(() =>
  applyMissingFilters(categoriesStore.categories, activeFilters.value, (category, filter) => {
    switch (filter) {
      case 'name':
        return !category.name || category.name.trim() === '';
      case 'metaTitle':
        return !category.metaTitle;
      case 'metaDescription':
        return !category.metaDescription;
      case 'description':
        return !(category.description && category.description.descriptionHtml);
      case 'productsPaths':
        return !(category.productsPaths && category.productsPaths.length > 0);
      case 'image':
        return !category.images || category.images.length === 0;
      default:
        return true;
    }
  })
);

// Use the new composable for URL filtering.
const urlFilters = useUrlFilters((url) => url.urlType === UrlTypes.CATEGORY);

// Expand processed panel by default (panel index 1)
const expandedPanels = ref([1]);
</script>
